/**
 * DevTestLogin — dev-v2 環境専用のテストユーザークイックログイン + テスト環境初期化。
 *
 * 表示条件: ビルド時の VITE_FIREBASE_PROJECT_ID が 'caud-goenhub-dev-v2' の場合のみ。
 * 本番ビルド（caud-goenhub）では条件が定数 false になり、バンドルから除去される。
 *
 * テストユーザーは scripts/src/seed-test-users.ts で投入する（変更時は両方更新）。
 * 初期化は devTestReset Callable（functions/src/common/devTestReset.ts）を呼び、
 * seed_demo_* データをスナップショット時点の状態に戻す。
 */
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { FlaskConical, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import { auth, functions } from '@/lib/firebase';

export const isDevTestLoginEnabled =
  import.meta.env.VITE_FIREBASE_PROJECT_ID === 'caud-goenhub-dev-v2';

const TEST_PASSWORD = 'GoEnHub-test2026';

/** 部署 3 種 × レベル 3 階層 = 全 9 ロールタイプ。 */
const TEST_USERS = [
  { email: 'test-admin@goenhub.test', label: '管理者リーダー', description: 'admin / leader' },
  {
    email: 'test-admin-employee@goenhub.test',
    label: '管理者社員',
    description: 'admin / employee',
  },
  {
    email: 'test-admin-external@goenhub.test',
    label: '管理者外部',
    description: 'admin / external',
  },
  { email: 'test-sales-leader@goenhub.test', label: '営業リーダー', description: 'sales / leader' },
  { email: 'test-sales@goenhub.test', label: '営業社員', description: 'sales / employee' },
  { email: 'test-sa@goenhub.test', label: '外部SA', description: 'sales / external' },
  { email: 'test-sa2@goenhub.test', label: '外部SA2', description: 'sales / external (2人目)' },
  {
    email: 'test-marketing-leader@goenhub.test',
    label: 'マーケリーダー',
    description: 'marketing / leader',
  },
  { email: 'test-staff@goenhub.test', label: 'マーケ社員', description: 'marketing / employee' },
  {
    email: 'test-marketing-external@goenhub.test',
    label: 'マーケ外部',
    description: 'marketing / external',
  },
] as const;

const devTestResetFn = httpsCallable<
  Record<string, never>,
  { ok: boolean; deletedDocs: number; restoredDocs: number }
>(functions, 'devTestReset');

/**
 * 初期化中はサインイン → コンポーネント再マウントが起きるため、
 * 結果メッセージは sessionStorage 経由で再マウント後も表示する。
 */
const RESULT_STORAGE_KEY = 'devTestLogin.lastResult';

function readStoredResult(): string | null {
  try {
    return sessionStorage.getItem(RESULT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeResult(message: string | null): void {
  try {
    if (message === null) sessionStorage.removeItem(RESULT_STORAGE_KEY);
    else sessionStorage.setItem(RESULT_STORAGE_KEY, message);
  } catch {
    /* storage 不可でも致命的ではない */
  }
}

export function DevTestLogin() {
  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfoState] = useState<string | null>(readStoredResult);
  const setInfo = (message: string | null) => {
    storeResult(message);
    setInfoState(message);
  };

  if (!isDevTestLoginEnabled) return null;

  const signInAs = async (email: string) => {
    setBusyEmail(email);
    setError(null);
    setInfo(null);
    try {
      await signInWithEmailAndPassword(auth, email, TEST_PASSWORD);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`テストログインに失敗しました: ${message}`);
    } finally {
      setBusyEmail(null);
    }
  };

  const resetTestData = async () => {
    if (
      !window.confirm(
        'テストデータ（seed_demo_*）を初期状態に戻します。テスト中に加えた変更は失われます。実行しますか？',
      )
    ) {
      return;
    }
    setResetting(true);
    setError(null);
    setInfo(null);
    // 初期化はサインインが必要 → 未サインインなら管理者テストで一時サインイン。
    // サインインするとこのコンポーネントは再マウントされるため、
    // 結果メッセージは sessionStorage に書いてからサインアウトして表示する。
    const wasSignedOut = !auth.currentUser;
    try {
      if (wasSignedOut) {
        await signInWithEmailAndPassword(auth, TEST_USERS[0].email, TEST_PASSWORD);
      }
      const result = await devTestResetFn({});
      setInfo(
        `初期化が完了しました（削除 ${result.data.deletedDocs} 件 / 復元 ${result.data.restoredDocs} 件）`,
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      storeResult(`初期化に失敗しました: ${message}`);
      setError(`初期化に失敗しました: ${message}`);
    } finally {
      if (wasSignedOut && auth.currentUser) {
        await signOut(auth).catch(() => undefined);
      }
      setResetting(false);
    }
  };

  return (
    <div className="mt-6 rounded-md border border-dashed border-[var(--accent-yellow,#eab308)] p-4">
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--text-secondary)]">
        <FlaskConical className="size-4" />
        <span>検証環境専用テストログイン（dev-v2）</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {TEST_USERS.map((u) => (
          <button
            key={u.email}
            type="button"
            disabled={busyEmail !== null || resetting}
            onClick={() => void signInAs(u.email)}
            className="rounded-sm border border-[var(--border)] bg-[var(--bg-root)] px-2 py-2 text-left text-xs hover:border-[var(--accent)] disabled:opacity-50 transition-colors"
          >
            <div className="font-semibold text-[var(--text-primary)]">
              {busyEmail === u.email ? 'ログイン中…' : u.label}
            </div>
            <div className="text-[10px] text-[var(--text-muted)]">{u.description}</div>
          </button>
        ))}
      </div>
      <button
        type="button"
        disabled={busyEmail !== null || resetting}
        onClick={() => void resetTestData()}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-sm border border-[var(--accent-red)] bg-[var(--bg-root)] px-3 py-2 text-xs font-semibold text-[var(--accent-red)] hover:bg-[var(--accent-red-subtle)] disabled:opacity-50 transition-colors"
      >
        <RotateCcw className="size-3.5" />
        {resetting ? '初期化中…' : 'テスト環境を初期状態に戻す'}
      </button>
      {info && (
        <div className="mt-3 rounded-sm bg-[var(--bg-root)] border border-[var(--border)] p-2 text-[11px] text-[var(--text-secondary)]">
          {info}
        </div>
      )}
      {error && (
        <div className="mt-3 rounded-sm bg-[var(--accent-red-subtle)] p-2 text-[11px] text-[var(--accent-red)]">
          {error}
        </div>
      )}
      <p className="mt-3 text-[10px] text-[var(--text-muted)]">
        本番環境ではこのパネルは表示されません。
      </p>
    </div>
  );
}
